import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '../../../../redux/hooks';
import { useOnFarmActionBlocked } from '../../../../hooks/useOnFarmActionBlocked';
import { DateInput, FilterDropdown, Modal, ValidatedInput } from '../../../../components';
import { defaultFilterOption, FilterOption } from '../../../../components/custom-input/FilterDropdown';
import { ValidatedState } from '../../../../components/custom-input/ValidatedInput';
import EmbryoDetailsTable, { hasDuplicateStrawNumbers, StrawRow } from '../../../../components/embryo-table/EmbryoDetailsTable';
import { AlertSVG, PlusCircleSvg } from '../../../../components/svgs';
import { RequireAlert } from '../../../inventory-management/InventoryManagementCollectDeposit';
import OnFarmAddAnimal from '../../animal-management/OnFarmAddAnimal';
import SelectStorageLocations from '../../../../components/select-storage-locations/SelectStorageLocations';
import { specimenUOM, Validators } from '../../../../types/enums';
import { Animal, SpecimenType, StorageLocation } from '../../../../types/interfaces';
import { getSpecimenTypes } from '../../../../api/specimensTypesApi';
import { hashCode } from '../../../../utils/commonUtils';
import { getOnFarmAccountAnimals, getOnFarmAnimalById } from '../../../../services/onFarmInventory.service';
import { showToast } from '../../../../services/toast.service';
import {
  createOnFarmDepositTransaction,
  isOnFarmDepositValid,
  mapStrawsToStorageCanisters,
  OnFarmDepositSelectedStates,
} from '../../../../services/onFarmDeposit.service';
import {
  BUTTON_CONSTANTS,
  INVENTORY_CONSTANTS,
  LABEL_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
  VALIDATION_ALERT_CONSTANTS,
} from '../../../../constants/common';
import { toastMessages } from '../../../../constants/errorMessages';
const initialValidatedState: ValidatedState = { value: '', valid: true };

const initialOnFarmStorageLocationState: StorageLocation = {
  site: { name: '', value: '' },
  tank: { name: '', value: '' },
  canister: { name: '', value: '' },
  quantity: { value: null },
  id: hashCode(new Date().toString()),
};

const unitOfMeasureOptions: FilterOption<specimenUOM>[] = Object.values(specimenUOM)
  .filter(item => item !== specimenUOM.Default)
  .map(item => ({ name: item, value: item }));

const toAnimalOption = (animal: Animal): FilterOption<Animal> => ({
  name: `${animal.code}-${animal.name}`,
  value: animal,
});

const emptyAnimalOption: FilterOption<Animal> = { name: '', value: undefined as unknown as Animal };

const createEmptyStrawRow = (index: number): StrawRow => ({
  embryoStrawNumber: (index + 1).toString(),
  embryoGrade: '',
  embryoStage: '',
  embryoCountPerStraw: 0,
  storageCanisterId: undefined,
});

const OnFarmSingleDeposit = (): JSX.Element => {
  const activeAccount = useAppSelector(state => state.activeAccount);
  const isOnFarmLocked = useOnFarmActionBlocked();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);
  const [isValidationAlertActive, setIsValidationAlertActive] = useState(false);
  const [addAnimalModal, setAddAnimalModal] = useState(false);
  const [animalOptions, setAnimalOptions] = useState<FilterOption<Animal>[]>([]);
  const [specimenTypes, setSpecimenTypes] = useState<FilterOption<SpecimenType>[]>([]);
  const [numStraws, setNumStraws] = useState(0);
  const [strawData, setStrawData] = useState<StrawRow[]>([]);
  const [strawValidationError, setStrawValidationError] = useState<string | null>(null);
  const [missingStrawError, setMissingStrawError] = useState<string | null>(null);
  const [selectedStates, setSelectedStates] = useState<OnFarmDepositSelectedStates>({
    storageLocations: [initialOnFarmStorageLocationState],
  });

  const isEmbryo = selectedStates.itemType?.value === 'Embryo' || selectedStates.specimensType?.name === 'Embryo';

  useEffect(() => {
    const loadSpecimenTypes = async () => {
      try {
        const { data } = await getSpecimenTypes();
        setSpecimenTypes(data.map((item: SpecimenType) => ({ name: item.name ?? '', value: item })));
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    };

    loadSpecimenTypes();
  }, []);

  useEffect(() => {
    const loadAccountAnimals = async () => {
      setAnimalOptions([]);
      if (!activeAccount?.accountId) {
        return;
      }

      try {
        const { data } = await getOnFarmAccountAnimals();
        setAnimalOptions(data.result.map(toAnimalOption));
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    };

    loadAccountAnimals();
  }, [activeAccount?.accountId]);

  useEffect(() => {
    setSelectedStates(prev => ({ ...prev, accountId: activeAccount?.accountId }));
  }, [activeAccount?.accountId]);

  const resetStrawFields = () => {
    setNumStraws(0);
    setStrawData([]);
    setStrawValidationError(null);
    setMissingStrawError(null);
  };

  const handleAnimalChange = (option: FilterOption<Animal>) => {
    const animal = option?.value;

    if (!animal) {
      resetStrawFields();
      setSelectedStates(prev => ({
        ...prev,
        animal: undefined,
        specimensType: undefined,
        itemType: { value: '', valid: false },
        embryoStraws: undefined,
      }));
      return;
    }

    const selectedAnimalType = animal.gender?.toLowerCase() === 'female' ? 'Embryo' : 'Semen';
    const matchedSpecimenType = specimenTypes.find(opt => opt.name?.toLowerCase() === selectedAnimalType.toLowerCase());
    const isFemaleAnimal = selectedAnimalType === 'Embryo';

    resetStrawFields();
    setSelectedStates(prev => ({
      ...prev,
      animal,
      itemType: { value: selectedAnimalType, valid: true },
      specimensType: matchedSpecimenType,
      embryoStraws: undefined,
      ...(isFemaleAnimal ? {} : { quantity: undefined }),
    }));
  };

  const handleAddAnimalClose = async (animalId?: number) => {
    setAddAnimalModal(false);
    if (!animalId) {
      return;
    }

    try {
      const { data: animal } = await getOnFarmAnimalById(animalId, { include: 'Breed.Species' });
      const animalOption = toAnimalOption(animal);
      setAnimalOptions(prev => (prev.some(option => option.value.animalId === animal.animalId) ? prev : [...prev, animalOption]));
      handleAnimalChange(animalOption);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  useEffect(() => {
    if (!selectedStates.animal || specimenTypes.length === 0) {
      return;
    }

    const selectedAnimalType = selectedStates.animal.gender?.toLowerCase() === 'female' ? 'Embryo' : 'Semen';
    const matchedSpecimenType = specimenTypes.find(opt => opt.name?.toLowerCase() === selectedAnimalType.toLowerCase());

    if (selectedAnimalType !== 'Embryo') {
      resetStrawFields();
    }

    setSelectedStates(prev => ({
      ...prev,
      itemType: { value: selectedAnimalType, valid: true },
      specimensType: matchedSpecimenType,
      ...(selectedAnimalType !== 'Embryo' ? { embryoStraws: undefined } : {}),
    }));
  }, [selectedStates.animal?.animalId, specimenTypes]);

  const selectedAnimalOption = selectedStates.animal
    ? animalOptions.find(option => option.value.animalId === selectedStates.animal?.animalId) ??
      toAnimalOption(selectedStates.animal)
    : emptyAnimalOption;

  const handleNumStrawsChange = (value: number) => {
    if (value > 0) {
      setNumStraws(value);

      let updated = [...strawData];
      if (value > strawData.length) {
        for (let index = strawData.length; index < value; index++) {
          updated.push(createEmptyStrawRow(index));
        }
      } else if (value < strawData.length) {
        updated = updated.slice(0, value);
      }

      updated = updated.map((row, index) => ({
        ...row,
        embryoStrawNumber: row.lockedCode ? row.embryoStrawNumber : (index + 1).toString(),
      }));

      setStrawData(updated);
      setSelectedStates(prev => ({ ...prev, embryoStraws: updated }));
      return;
    }

    setNumStraws(value);
    setStrawData([]);
    setSelectedStates(prev => ({ ...prev, embryoStraws: undefined }));
  };

  const handleStrawDataChange = (updatedRows: StrawRow[]) => {
    setStrawData(updatedRows);
    setNumStraws(updatedRows.length);

    const totalQuantity = updatedRows.reduce((acc, straw) => acc + (straw.embryoCountPerStraw || 0), 0);
    setSelectedStates(prev => ({
      ...prev,
      embryoStraws: updatedRows,
      quantity: {
        value: totalQuantity.toString(),
        valid: totalQuantity > 0,
      },
    }));
  };

  const strawCodeOptions: FilterOption<string>[] = isEmbryo
    ? strawData.map(straw => {
        const quantityLabel = straw.embryoCountPerStraw ? ` (Qty: ${straw.embryoCountPerStraw})` : '';
        return {
          name: `${straw.embryoStrawNumber}${quantityLabel}`,
          value: straw.embryoStrawNumber,
          quantity: straw.embryoCountPerStraw,
        };
      })
    : [];

  const isValidForm = useCallback(() => {
    const mappedStraws = isEmbryo
      ? mapStrawsToStorageCanisters(strawData, selectedStates.storageLocations)
      : selectedStates.embryoStraws;

    return isOnFarmDepositValid({
      ...selectedStates,
      embryoStraws: mappedStraws,
    });
  }, [isEmbryo, selectedStates, strawData]);

  const isStorageQuantityMismatch = useCallback(() => {
    const quantityValue = Number(selectedStates.quantity?.value ?? 0);
    if (!selectedStates.quantity?.valid || quantityValue <= 0) {
      return false;
    }

    const storageQuantityTotal =
      selectedStates.storageLocations?.reduce((total, location) => total + Number(location.quantity.value ?? 0), 0) ?? 0;

    return storageQuantityTotal !== quantityValue;
  }, [selectedStates.quantity, selectedStates.storageLocations]);

  const resetForm = () => {
    setSelectedStates({
      accountId: activeAccount?.accountId,
      storageLocations: [
        {
          ...initialOnFarmStorageLocationState,
          id: hashCode(new Date().toString()),
        },
      ],
    });
    setNumStraws(0);
    setStrawData([]);
    setStrawValidationError(null);
    setMissingStrawError(null);
    setIsValidationAlertActive(false);
    setFormResetKey(prev => prev + 1);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isOnFarmLocked) {
      return;
    }
    setStrawValidationError(null);
    setMissingStrawError(null);

    let mappedStraws = selectedStates.embryoStraws ?? [];

    if (isEmbryo) {
      const invalidRows = strawData.some(strawRow => !strawRow.embryoCountPerStraw || strawRow.embryoCountPerStraw <= 0);
      if (invalidRows || strawData.length === 0) {
        setIsValidationAlertActive(true);
        setStrawValidationError(VALIDATION_ALERT_CONSTANTS.MISSING_QUANTITY);
        showToast.error(toastMessages.MISSING_REQUIRED_FIELDS);
        return;
      }
      if (hasDuplicateStrawNumbers(strawData)) {
        setIsValidationAlertActive(true);
        setStrawValidationError(VALIDATION_ALERT_CONSTANTS.UNIQUE_STRAW_NUMBER);
        showToast.error(toastMessages.MISSING_REQUIRED_FIELDS);
        return;
      }

      mappedStraws = mapStrawsToStorageCanisters(strawData, selectedStates.storageLocations);
      const missingCanisterStraws = mappedStraws.filter(straw => !straw.storageCanisterId);
      if (missingCanisterStraws.length > 0) {
        setIsValidationAlertActive(true);
        setMissingStrawError(VALIDATION_ALERT_CONSTANTS.MISSING_STORAGE_LOCATION);
        showToast.error(toastMessages.MISSING_REQUIRED_FIELDS);
        return;
      }
    }

    const payloadStates: OnFarmDepositSelectedStates = {
      ...selectedStates,
      embryoStraws: isEmbryo ? mappedStraws : undefined,
    };

    if (!isOnFarmDepositValid(payloadStates)) {
      setIsValidationAlertActive(true);
      if (isStorageQuantityMismatch()) {
        showToast.error(toastMessages.TOTAL_QTY_MISMATCH);
      } else {
        showToast.error(toastMessages.MISSING_REQUIRED_FIELDS);
      }
      return;
    }

    try {
      setIsSubmitting(true);
      await createOnFarmDepositTransaction(payloadStates);
      showToast.success(toastMessages.DEPOSIT_SUCCESSFUL);
      resetForm();
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <form key={formResetKey} action="submit" onSubmit={handleSubmit}>
        <div className="form-row">
          <label>
            <div>
              *{INVENTORY_CONSTANTS.ANIMAL}:
              <RequireAlert isActive={isValidationAlertActive && !selectedStates.animal} />
            </div>
          </label>
          <div className="input-container animal-container">
            <FilterDropdown
              placeholder="Select Animal"
              options={animalOptions}
              value={selectedAnimalOption}
              disabled={isOnFarmLocked || animalOptions.length === 0}
              onChange={handleAnimalChange}
            />
            <button type="button" className="button" disabled={isOnFarmLocked} onClick={() => setAddAnimalModal(true)}>
              <div className="add-animal-button">
                <PlusCircleSvg />
                &nbsp;&nbsp;{BUTTON_CONSTANTS.ADD_ANIMAL}
              </div>
            </button>
          </div>
        </div>
        <div className="form-row">
          <ValidatedInput
            disabled
            label={
              <div>
                *{INVENTORY_CONSTANTS.ITEM_TYPE}:
                <RequireAlert isActive={isValidationAlertActive && !selectedStates.itemType?.value} />
              </div>
            }
            placeholder="Type"
            setValidatedState={itemType => setSelectedStates(prev => ({ ...prev, itemType }))}
            validators={[]}
            validatedStateForAutoFill={selectedStates.itemType ?? { value: '', valid: false }}
          />
        </div>
        {isEmbryo && (
          <div className="form-row">
            <ValidatedInput
              label={`*${LABEL_CONSTANTS.NUMBER_OF_STRAWS}`}
              placeholder="0"
              type="number"
              min={0}
              setValidatedState={value => handleNumStrawsChange(Number(value.value) || 0)}
              validators={[Validators.REQUIRED]}
              validate={isValidationAlertActive}
              validatedStateForAutoFill={{ value: String(numStraws || ''), valid: numStraws > 0 }}
            />
          </div>
        )}
        <div className="form-row">
          <label>
            <div>
              *{INVENTORY_CONSTANTS.ITEM_UNIT_MEASURE}:
              <RequireAlert isActive={isValidationAlertActive && !selectedStates.specimenUOM?.value} />
            </div>
          </label>
          <div className="input-container">
            <FilterDropdown
              placeholder="Select UoM"
              options={unitOfMeasureOptions}
              value={selectedStates.specimenUOM ?? (defaultFilterOption as FilterOption<specimenUOM>)}
              onChange={option => setSelectedStates(prev => ({ ...prev, specimenUOM: option }))}
            />
          </div>
        </div>
        {isEmbryo && Number(numStraws) > 0 && (
          <div className="straw-distribution-section">
            <label className="form-label">*{INVENTORY_CONSTANTS.STRAW_DISTRIBUTION}:</label>
            <EmbryoDetailsTable data={strawData} onChange={updatedRows => handleStrawDataChange(updatedRows)} />
            {strawValidationError && (
              <div className="validated-input-message-error">
                <AlertSVG />
                {strawValidationError}
              </div>
            )}
          </div>
        )}
        <div className="form-row">
          <label>
            <div>
              *{INVENTORY_CONSTANTS.LOT_DATE}:
              <RequireAlert isActive={isValidationAlertActive && !selectedStates.freezeDate} />
            </div>
          </label>
          <div className="input-container">
            <DateInput
              value={selectedStates.freezeDate}
              onChange={freezeDate => setSelectedStates(prev => ({ ...prev, freezeDate }))}
              maxDate={new Date()}
            />
          </div>
        </div>
        <div className="form-row">
          <ValidatedInput
            label={TABLE_HEADER_CONSTANTS.LOT_NO}
            placeholder="Lot Number"
            setValidatedState={lotNumber => setSelectedStates(prev => ({ ...prev, lotNumber }))}
            validators={[]}
            validatedStateForAutoFill={selectedStates.lotNumber ?? { value: '', valid: true }}
          />
        </div>
        <div className="form-row">
          <ValidatedInput
            label={INVENTORY_CONSTANTS.COLLECTION_SITE}
            placeholder="Collection Site"
            setValidatedState={collectionSite => setSelectedStates(prev => ({ ...prev, collectionSite }))}
            validators={[]}
            validatedStateForAutoFill={selectedStates.collectionSite ?? initialValidatedState}
          />
        </div>
        <div className="form-row">
          <ValidatedInput
            label={isEmbryo ? INVENTORY_CONSTANTS.VETERINARIAN : INVENTORY_CONSTANTS.COLLECTED_BY}
            placeholder={isEmbryo ? INVENTORY_CONSTANTS.VETERINARIAN : 'Collected By'}
            setValidatedState={collectedBy => setSelectedStates(prev => ({ ...prev, collectedBy }))}
            validators={[]}
            validatedStateForAutoFill={selectedStates.collectedBy ?? initialValidatedState}
          />
        </div>
        <div className="form-row">
          <ValidatedInput
            label={isEmbryo ? INVENTORY_CONSTANTS.EMBRYOLOGIST : INVENTORY_CONSTANTS.PROCESSED_BY}
            placeholder={isEmbryo ? INVENTORY_CONSTANTS.EMBRYOLOGIST : 'Processed By'}
            setValidatedState={processedBy => setSelectedStates(prev => ({ ...prev, processedBy }))}
            validators={[]}
            validatedStateForAutoFill={selectedStates.processedBy ?? initialValidatedState}
          />
        </div>
        <div className="form-row">
          <ValidatedInput
            label={isEmbryo ? INVENTORY_CONSTANTS.TECHNICIAN : INVENTORY_CONSTANTS.FROZEN_BY}
            placeholder={isEmbryo ? INVENTORY_CONSTANTS.TECHNICIAN : 'Frozen By'}
            setValidatedState={frozenBy => setSelectedStates(prev => ({ ...prev, frozenBy }))}
            validators={[]}
            validatedStateForAutoFill={selectedStates.frozenBy ?? initialValidatedState}
          />
        </div>
        <div className="form-row">
          <ValidatedInput
            label={`*${LABEL_CONSTANTS.QUANTITY}:`}
            placeholder="0"
            type="number"
            min={0}
            setValidatedState={quantity => setSelectedStates(prev => ({ ...prev, quantity }))}
            validators={[Validators.REQUIRED]}
            validate={isValidationAlertActive}
            validatedStateForAutoFill={selectedStates.quantity ?? { value: '', valid: true }}
          />
        </div>
        <div className="form-row expanded-text-area">
          <label htmlFor="on-farm-deposit-notes">{LABEL_CONSTANTS.NOTES}</label>
          <textarea
            id="on-farm-deposit-notes"
            name="notes"
            placeholder="Notes"
            value={selectedStates.notes ?? ''}
            onChange={event => setSelectedStates(prev => ({ ...prev, notes: event.target.value }))}
            rows={4}
            cols={50}
          />
        </div>
        <h2>{LABEL_CONSTANTS.STORAGE_LOCATION}</h2>
        <SelectStorageLocations
          selectedStorageLocations={selectedStates.storageLocations ?? [initialOnFarmStorageLocationState]}
          isValidationAlertActive={isValidationAlertActive}
          setSelectedStorageLocations={(storageLocations: StorageLocation[]) =>
            setSelectedStates(prev => ({ ...prev, storageLocations }))
          }
          quantityToMatch={Number(selectedStates.quantity?.value ?? 0)}
          itemType={selectedStates.itemType?.value ?? ''}
          strawCodeOptions={strawCodeOptions}
          activeLocationsOnly
          useAccountStorage
        />
        {missingStrawError && (
          <div className="validated-input-message-error">
            <AlertSVG />
            {missingStrawError}
          </div>
        )}
        <div className="submit-button">
          <button
            type="submit"
            disabled={isOnFarmLocked || isSubmitting}
            className={
              !isOnFarmLocked && isValidForm() && !isSubmitting ? 'button small green' : 'disabled button small green'
            }>
            {isSubmitting ? 'Submitting...' : BUTTON_CONSTANTS.SUBMIT}
          </button>
        </div>
      </form>
      <Modal isOpen={addAnimalModal} onClose={() => setAddAnimalModal(false)} width={1014} ignoreBackdrop>
        <OnFarmAddAnimal onCancel={handleAddAnimalClose} />
      </Modal>
    </>
  );
};

export default OnFarmSingleDeposit;
